import { ThemeProvider } from "@mui/material"
import theme from "./theme"
import { RouterProvider, createBrowserRouter } from "react-router-dom"
import { Navigation } from "./Navigation"
import { AuthProvider, useAuth, login, refresh, getUserData, getTokenExpireDate } from "./auth"
import DataProvider from "./DataProvider"
import { DataFetchingAuthContext } from "@inmagik/react-crud"
import CssBaseline from "@mui/material/CssBaseline"
import { SnackbarProvider } from "notistack"
import { StyledSnackbarContent } from "./utils/notisnack-integration"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import "dayjs/locale/it"
import { useTranslation } from "react-i18next"
import { useEffect } from "react"
import { createI18n } from "./i18n"
import { setLocale } from "yup"
import { it, en } from "yup-locales"

function ProvideAuthToDataFetching({ children }: { children: React.ReactNode }) {
  const { tokens } = useAuth()
  const { i18n } = useTranslation()

  const authHeaders: Record<string, string> = tokens?.access
    ? {
        Authorization: `Bearer ${tokens?.access}`,
        "Accept-Language": i18n.language,
        "Accept-Language-Header": i18n.language,
        "X-Lang": i18n.language,
      }
    : {}
  return <DataFetchingAuthContext.Provider value={authHeaders}>{children}</DataFetchingAuthContext.Provider>
}

function Root() {
  const i18n = createI18n(window.location.pathname)

  if (i18n.language === "it") {
    setLocale(it)
  } else {
    setLocale(en)
  }

  useEffect(() => {
    document.documentElement.lang = i18n.language
  }, [i18n.language])

  return (
    <ThemeProvider theme={theme}>
      <AuthProvider login={login} refresh={refresh} getUserData={getUserData} getTokenExpireDate={getTokenExpireDate}>
        <ProvideAuthToDataFetching>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
            <DataProvider>
              <SnackbarProvider
                Components={{
                  success: StyledSnackbarContent,
                  error: StyledSnackbarContent,
                  warning: StyledSnackbarContent,
                  info: StyledSnackbarContent,
                }}
              >
                <CssBaseline />
                <Navigation></Navigation>
              </SnackbarProvider>
            </DataProvider>
          </LocalizationProvider>
        </ProvideAuthToDataFetching>
      </AuthProvider>
    </ThemeProvider>
  )
}

const router = createBrowserRouter([{ path: "*", element: <Root /> }])

function App() {
  return <RouterProvider router={router}></RouterProvider>
}

export default App
